/**
 * Статусная отметка: цвет + ИКОНКА + ПОДПИСЬ, и никогда цвет отдельно.
 *
 * Компонент один на всё приложение по той же причине, что и `DoneSplit`:
 * состояние обязано выглядеть одинаково на карте, в очереди проверок, в
 * карточке техники и в легенде доли. Разные отметки одного состояния читаются
 * как разные состояния.
 *
 * Что зафиксировано формой, а не договорённостью (ADR-0009):
 *
 * * **цвет не носит смысл в одиночку.** У каждого состояния своя форма иконки
 *   и своя подпись; в чёрно-белой печати и при дальтонизме отметка остаётся
 *   читаемой. Спрятать подпись можно только режимом `compact`, и она остаётся
 *   в доступном имени — совсем убрать её компонент не даёт;
 * * **красный означает КРИТИЧНОЕ СОСТОЯНИЕ объекта, а не оценку.** Состояния
 *   «без отчёта» и «впереди» физически не могут получить красный: у них свои
 *   нейтральные ступени;
 * * **«без отчёта» — это отсутствие данных.** Его подпись говорит про молчание
 *   источника, а не про невыполненную работу;
 * * **медь — только кандидатность.** Ни одно состояние светофора медь не берёт.
 */

import type { ComponentType, ReactNode } from "react";
import {
  CalendarClock,
  CircleCheck,
  CircleDashed,
  CircleDot,
  Diamond,
  Droplets,
  OctagonAlert,
  ShieldCheck,
  TriangleAlert,
} from "lucide-react";
import { defineMessages, type Message } from "../i18n/define";
import { tr } from "../i18n/runtime";
import styles from "./statusmark.module.css";

/** Состояния слоя данных. Порядок — от «всё хорошо» к «требует внимания». */
export type DataStatus =
  | "ok"
  | "verified"
  | "progress"
  | "warning"
  | "critical"
  | "missing"
  | "future"
  | "candidate"
  | "water";

export interface StatusSpec {
  status: DataStatus;
  /** Короткая подпись отметки на языке интерфейса. */
  label: string;
  /** Что состояние утверждает. Живёт в подсказке и в легенде. */
  meaning: string;
  icon: ComponentType<{ size?: number; strokeWidth?: number; "aria-hidden"?: boolean }>;
  className: string;
}

const TEXT = defineMessages({
  okLabel: { id: "common.kit.status.ok.label", defaultMessage: "отчитались", description: "Подпись статусной отметки: исполнитель отчитался; строчная, короткая, рядом с иконкой" },
  okMeaning: { id: "common.kit.status.ok.meaning", defaultMessage: "Заявление исполнителя: отчёт поступил.", description: "Подсказка и расшифровка в легенде к отметке «отчитались»" },
  verifiedLabel: { id: "common.kit.status.verified.label", defaultMessage: "проверено", description: "Подпись статусной отметки: проверено инспектором; строчная, короткая" },
  verifiedMeaning: { id: "common.kit.status.verified.meaning", defaultMessage: "Независимое подтверждение проверяющим.", description: "Подсказка и расшифровка в легенде к отметке «проверено»" },
  progressLabel: { id: "common.kit.status.progress.label", defaultMessage: "идёт", description: "Подпись статусной отметки: окно работ открыто; строчная, короткая" },
  progressMeaning: { id: "common.kit.status.progress.meaning", defaultMessage: "Окно работ открыто, срок ещё не истёк.", description: "Подсказка и расшифровка в легенде к отметке «идёт»" },
  warningLabel: { id: "common.kit.status.warning.label", defaultMessage: "закрывается", description: "Подпись статусной отметки: окно скоро закроется; строчная, короткая" },
  warningMeaning: { id: "common.kit.status.warning.meaning", defaultMessage: "Окно закрывается: осталось не больше пяти дней.", description: "Подсказка и расшифровка в легенде к отметке «закрывается»" },
  criticalLabel: { id: "common.kit.status.critical.label", defaultMessage: "критично", description: "Подпись статусной отметки: критичное состояние объекта; строчная, короткая" },
  criticalMeaning: { id: "common.kit.status.critical.meaning", defaultMessage: "Критичное состояние объекта: красный карантинный статус, просроченный осмотр, закрывшееся окно. Это состояние объекта, а не оценка людей.", description: "Подсказка и расшифровка в легенде к отметке «критично»" },
  missingLabel: { id: "common.kit.status.missing.label", defaultMessage: "без отчёта", description: "Подпись статусной отметки: отчёта нет (это отсутствие данных, не вина); строчная, короткая" },
  missingMeaning: { id: "common.kit.status.missing.meaning", defaultMessage: "Отчёта не поступало. Это отсутствие данных, а не подтверждённое невыполнение.", description: "Подсказка и расшифровка в легенде к отметке «без отчёта»" },
  futureLabel: { id: "common.kit.status.future.label", defaultMessage: "впереди", description: "Подпись статусной отметки: окно работы ещё не открылось; строчная, короткая" },
  futureMeaning: { id: "common.kit.status.future.meaning", defaultMessage: "Окно ещё не открылось: спрашивать не с чего.", description: "Подсказка и расшифровка в легенде к отметке «впереди»" },
  candidateLabel: { id: "common.kit.status.candidate.label", defaultMessage: "кандидат", description: "Подпись статусной отметки: кандидатная связь или геометрия, ещё не принятая; строчная, короткая" },
  candidateMeaning: { id: "common.kit.status.candidate.meaning", defaultMessage: "Кандидатная связь или геометрия: до принятия она не повышается до факта.", description: "Подсказка и расшифровка в легенде к отметке «кандидат»" },
  waterLabel: { id: "common.kit.status.water.label", defaultMessage: "вода", description: "Подпись статусной отметки: ирригация (вода запрошена и подана); строчная, короткая" },
  waterMeaning: { id: "common.kit.status.water.meaning", defaultMessage: "Ирригация: запрошено и подано.", description: "Подсказка и расшифровка в легенде к отметке «вода»" },
});

interface SpecSource {
  label: Message;
  meaning: Message;
  icon: StatusSpec["icon"];
  className: string;
}

const SPECS: Record<DataStatus, SpecSource> = {
  ok: { label: TEXT.okLabel, meaning: TEXT.okMeaning, icon: CircleCheck, className: styles.ok ?? "" },
  verified: { label: TEXT.verifiedLabel, meaning: TEXT.verifiedMeaning, icon: ShieldCheck, className: styles.verified ?? "" },
  progress: { label: TEXT.progressLabel, meaning: TEXT.progressMeaning, icon: CircleDot, className: styles.progress ?? "" },
  warning: { label: TEXT.warningLabel, meaning: TEXT.warningMeaning, icon: TriangleAlert, className: styles.warning ?? "" },
  critical: { label: TEXT.criticalLabel, meaning: TEXT.criticalMeaning, icon: OctagonAlert, className: styles.critical ?? "" },
  missing: { label: TEXT.missingLabel, meaning: TEXT.missingMeaning, icon: CircleDashed, className: styles.missing ?? "" },
  future: { label: TEXT.futureLabel, meaning: TEXT.futureMeaning, icon: CalendarClock, className: styles.future ?? "" },
  candidate: { label: TEXT.candidateLabel, meaning: TEXT.candidateMeaning, icon: Diamond, className: styles.candidate ?? "" },
  water: { label: TEXT.waterLabel, meaning: TEXT.waterMeaning, icon: Droplets, className: styles.water ?? "" },
};

/** Порядок отметок в легендах — он же порядок чтения состояния. */
export const STATUS_ORDER: readonly DataStatus[] = [
  "ok",
  "verified",
  "progress",
  "warning",
  "critical",
  "missing",
  "future",
];

export function statusSpec(status: DataStatus): StatusSpec {
  const source = SPECS[status];
  return {
    status,
    label: tr(source.label),
    meaning: tr(source.meaning),
    icon: source.icon,
    className: source.className,
  };
}

export function StatusMark({
  status,
  label,
  compact = false,
  title,
}: {
  status: DataStatus;
  /** Своя подпись состояния на этом экране. По умолчанию — каноническая. */
  label?: ReactNode;
  /**
   * Подпись уезжает в доступное имя: применимо ТОЛЬКО там, где видимый текст
   * стоит вплотную (сегмент доли, ячейка таблицы со своей подписью).
   */
  compact?: boolean;
  title?: string;
}) {
  const spec = statusSpec(status);
  const Icon = spec.icon;
  const text = label ?? spec.label;
  return (
    <span
      className={`${styles.mark} ${spec.className} ${compact ? styles.compact : ""}`}
      title={title ?? spec.meaning}
    >
      <Icon size={compact ? 14 : 16} strokeWidth={1.75} aria-hidden />
      <span className={compact ? styles.srOnly : styles.label}>{text}</span>
    </span>
  );
}

/**
 * Легенда состояний. Она не «расшифровка цвета»: рядом с каждой отметкой стоит
 * то, что состояние утверждает, — иначе светофор читается как оценка людей.
 */
export function StatusLegend({
  statuses,
  withMeaning = false,
}: {
  statuses: readonly DataStatus[];
  withMeaning?: boolean;
}) {
  return (
    <ul className={styles.legend}>
      {statuses.map((status) => {
        const spec = statusSpec(status);
        return (
          <li key={status} className={styles.legendItem}>
            <StatusMark status={status} />
            {withMeaning ? (
              <span className={styles.legendMeaning}>{spec.meaning}</span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

export const statusMarkStyles = styles;
